import React, { useState } from 'react';
import { Eye, CheckCircle, XCircle, User, Building2, ChevronDown, ChevronUp, Clock, RefreshCw } from 'lucide-react';
import { DeleteRequest } from '../types';
import { Badge } from './Badge';

interface AdminDeleteRequestTableProps {
  requests: DeleteRequest[];
  loading: boolean;
  onViewDetails: (requestId: number) => void;
  onApprove: (requestId: number) => void;
  onReject: (requestId: number) => void;
  onReactivate: (requestId: number) => void;
  selectedRequestIds?: number[];
  onSelectionChange?: (selectedIds: number[]) => void;
}

export const AdminDeleteRequestTable: React.FC<AdminDeleteRequestTableProps> = ({
  requests,
  loading,
  onViewDetails,
  onApprove,
  onReject,
  onReactivate,
  selectedRequestIds = [],
  onSelectionChange,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const allPendingSelected = pendingRequests.length > 0 &&
    pendingRequests.every(r => selectedRequestIds.includes(r.requestId));

  const handleSelectAll = () => {
    if (!onSelectionChange) return;

    if (allPendingSelected) {
      onSelectionChange([]);
    } else {
      const pendingIds = pendingRequests.map(r => r.requestId);
      onSelectionChange(pendingIds);
    }
  };

  const handleSelectRow = (requestId: number) => {
    if (!onSelectionChange) return;

    if (selectedRequestIds.includes(requestId)) {
      onSelectionChange(selectedRequestIds.filter(id => id !== requestId));
    } else {
      onSelectionChange([...selectedRequestIds, requestId]);
    }
  };

  const toggleRow = (requestId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(requestId)) {
      newExpanded.delete(requestId);
    } else {
      newExpanded.add(requestId);
    }
    setExpandedRows(newExpanded);
  };

  const getStatusBadgeColor = (status: string): 'warning' | 'success' | 'error' | 'primary' => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'primary';
    }
  };

  const getTypeBadgeColor = (type: string): 'primary' | 'success' => {
    return type === 'provider' ? 'success' : 'primary';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading delete requests...</p>
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-12 text-center">
          <XCircle className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 text-lg font-medium">No delete requests found</p>
          <p className="text-slate-500 text-sm mt-2">Try adjusting your filters</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {onSelectionChange && (
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allPendingSelected}
                    onChange={handleSelectAll}
                    disabled={pendingRequests.length === 0}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                    title="Select all pending requests"
                  />
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                User Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Reason
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Requested
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {requests.map((request) => {
              const isSelected = selectedRequestIds.includes(request.requestId);
              const isPending = request.status === 'pending';

              return (
                <React.Fragment key={request.requestId}>
                  <tr className={`hover:bg-slate-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}>
                    {onSelectionChange && (
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectRow(request.requestId)}
                          disabled={!isPending}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
                          title={isPending ? 'Select request' : 'Only pending requests can be selected'}
                        />
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-slate-900">
                        #{request.requestId}
                      </span>
                    </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={getTypeBadgeColor(request.accountType)}>
                      {request.accountType === 'provider' ? (
                        <Building2 className="h-3 w-3 mr-1" />
                      ) : (
                        <User className="h-3 w-3 mr-1" />
                      )}
                      {request.accountType === 'provider' ? 'Provider' : 'User'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-slate-900">{request.name}</span>
                      <span className="text-xs text-slate-500">{request.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      <p className="text-sm text-slate-700 line-clamp-2">
                        {request.reason || 'No reason provided'}
                      </p>
                      {request.reason && request.reason.length > 80 && (
                        <button
                          onClick={() => toggleRow(request.requestId)}
                          className="text-xs text-blue-600 hover:text-blue-700 mt-1 flex items-center gap-1"
                        >
                          {expandedRows.has(request.requestId) ? (
                            <>
                              Show less <ChevronUp className="h-3 w-3" />
                            </>
                          ) : (
                            <>
                              Show more <ChevronDown className="h-3 w-3" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <Clock className="h-4 w-4" />
                      {formatDate(request.createdAt)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={getStatusBadgeColor(request.status)}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onViewDetails(request.requestId)}
                        className="text-slate-600 hover:text-slate-900 transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      {request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => onApprove(request.requestId)}
                            className="text-green-600 hover:text-green-700 transition-colors"
                            title="Approve Request"
                          >
                            <CheckCircle className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => onReject(request.requestId)}
                            className="text-red-600 hover:text-red-700 transition-colors"
                            title="Reject Request"
                          >
                            <XCircle className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      {request.status === 'approved' && (
                        <button
                          onClick={() => onReactivate(request.requestId)}
                          className="text-emerald-600 hover:text-emerald-700 transition-colors"
                          title="Reactivate Account"
                        >
                          <RefreshCw className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                  </tr>
                  {expandedRows.has(request.requestId) && (
                    <tr>
                      <td colSpan={onSelectionChange ? 8 : 7} className="px-6 py-4 bg-slate-50">
                        <div className="max-w-3xl">
                          <h4 className="text-sm font-semibold text-slate-900 mb-2">Full Reason:</h4>
                          <p className="text-sm text-slate-700">{request.reason || 'No reason provided'}</p>
                          {request.rejectionReason && (
                            <div className="mt-4">
                              <h4 className="text-sm font-semibold text-red-900 mb-2">Rejection Reason:</h4>
                              <p className="text-sm text-red-700">{request.rejectionReason}</p>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
